import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { LOGO_URL, VYOMRA_LOGO_URL, PLATFORM_NAME } from "@/lib/brand";
import { homeFor } from "@/lib/roleHome";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);   // { name, address, phones[], logo_url, hospital_id }
  // Sniff the host so the backend can resolve tenant even when the preview
  // is served via *.preview.emergentagent.com (which never carries the real
  // production subdomain).  Falls back to the current hostname otherwise.
  useEffect(() => {
    const host = window.location.hostname;
    axios.get(`${BACKEND}/api/branding`, { params: { host } })
      .then((r) => setBrand(r.data))
      .catch(() => setBrand(null));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome · ${u.role.replace("_", " ")}`);
      nav(homeFor(u.role), { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const hospitalName = brand?.name || PLATFORM_NAME;
  const tagline = brand?.tagline || "MULTI SPECIALITY HOSPITAL";
  // Platform-level identity on Login / Dashboard uses the Vyomra HOS mark.
  // Hospital-specific print templates continue to use the tenant `brand.logo_url`.
  const logoSrc = VYOMRA_LOGO_URL;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1754538917023-8e4299a51ab0?w=1200"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="bg-white/95 inline-block p-4 mb-5 rounded-sm">
            <img src={logoSrc} alt={hospitalName} className="h-16 object-contain" data-testid="login-hospital-logo"/>
          </div>
          <div className="text-[11px] tracking-[0.25em] text-slate-300 mb-2 uppercase" data-testid="login-hospital-tag">{tagline}</div>
          <h1 className="font-display text-3xl font-medium" data-testid="login-hospital-name">{hospitalName}</h1>
          <p className="text-slate-300 mt-2 text-sm max-w-md">
            Complete OPD, IPD, Emergency, Pharmacy, Lab, Billing and Discharge — connected under one patient UHID.
          </p>
          {brand?.address && (
            <p className="text-slate-400 mt-3 text-xs">{brand.address}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <img src={logoSrc} alt={hospitalName} className="h-12 object-contain" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{tagline}</div>
              <div className="font-semibold text-slate-900">{hospitalName}</div>
            </div>
          </div>
          <h2 className="font-display text-2xl font-semibold text-slate-900 tracking-tight">
            Sign in to <span className="text-blue-800">{brand ? hospitalName : PLATFORM_NAME}</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {brand ? "Only accounts under this hospital may sign in here." : `Access ${PLATFORM_NAME} — Hospital Operations & Management System.`}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="label-tiny">Email</Label>
              <Input data-testid="login-email" className="mt-1 border-slate-300" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label className="label-tiny">Password</Label>
              <Input data-testid="login-password" type="password" className="mt-1 border-slate-300" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button data-testid="login-submit" type="submit" className="w-full bg-blue-700 hover:bg-blue-800 h-10 rounded-sm" disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign In
            </Button>
          </form>

          <div className="mt-10 flex items-center justify-between text-[10px] tracking-widest text-slate-400 uppercase">
            <span>Authorised personnel only</span>
            <span className="text-slate-400">Powered by <b className="text-slate-600">Vyomra</b></span>
          </div>
        </div>
      </div>
    </div>
  );
}
